import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, X, Loader2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

interface PendingRow {
  id: string;
  student_id: string;
  session_id: string | null;
  status: string;
  scanned_at: string;
  students: { first_name: string; last_name: string; student_number: string } | null;
  attendance_sessions: { topic: string | null; module_code: string | null; class: string | null; session_date: string } | null;
}

export function PendingApprovalsQueue() {
  const { hasRole } = useAuth();
  const { toast } = useToast();
  const [rows, setRows] = useState<PendingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const isAcademic = hasRole("head_academic") || hasRole("superadmin");
  const isHod = hasRole("head_administration") || hasRole("superadmin");

  const load = useCallback(async () => {
    setLoading(true);
    const statuses: string[] = [];
    if (isAcademic) statuses.push("pending_approval");
    if (isHod) statuses.push("pending_hod_confirm");
    if (statuses.length === 0) { setRows([]); setLoading(false); return; }

    const { data, error } = await supabase
      .from("attendance_records")
      .select("id, student_id, session_id, status, scanned_at, students!inner(first_name, last_name, student_number), attendance_sessions(topic, module_code, class, session_date)")
      .in("status", statuses)
      .eq("attendance_type", "session")
      .order("scanned_at", { ascending: true });

    if (error) toast({ title: "Failed to load", description: error.message, variant: "destructive" });
    setRows((data ?? []) as any);
    setLoading(false);
  }, [isAcademic, isHod, toast]);

  useEffect(() => { load(); }, [load]);

  async function approve(row: PendingRow) {
    setBusyId(row.id);
    const { error } = await supabase
      .from("attendance_records")
      .update({ status: "present", approved_by: (await supabase.auth.getUser()).data.user?.id, approved_at: new Date().toISOString() })
      .eq("id", row.id);
    setBusyId(null);
    if (error) return toast({ title: "Approve failed", description: error.message, variant: "destructive" });
    toast({ title: "Approved" });
    load();
  }

  async function reject(row: PendingRow) {
    setBusyId(row.id);
    const reason = rejectReason.trim() || "Rejected";
    const { error } = await supabase.from("attendance_records").delete().eq("id", row.id);
    setBusyId(null);
    setRejectReason("");
    if (error) return toast({ title: "Reject failed", description: error.message, variant: "destructive" });
    toast({ title: "Rejected", description: reason });
    load();
  }

  // Group by session
  const grouped = rows.reduce<Record<string, PendingRow[]>>((acc, r) => {
    const key = r.session_id ?? "none";
    (acc[key] ||= []).push(r);
    return acc;
  }, {});

  if (!isAcademic && !isHod) {
    return <Card><CardContent className="text-center text-muted-foreground py-12">You don't have permission to approve session attendance.</CardContent></Card>;
  }

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;
  if (rows.length === 0) return <Card><CardContent className="text-center text-muted-foreground py-12">No pending approvals.</CardContent></Card>;

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([sid, list]) => {
        const s = list[0].attendance_sessions;
        return (
          <Card key={sid}>
            <CardContent className="py-4 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{list[0].status === "pending_hod_confirm" ? "Pending HOD" : "Pending Academic Officer"}</Badge>
                <span className="font-semibold">{s?.topic || "Untitled"}</span>
                <span className="text-xs text-muted-foreground">{s?.module_code} • {s?.class} • {s?.session_date}</span>
                <Badge variant="outline" className="ml-auto">{list.length} record{list.length !== 1 ? "s" : ""}</Badge>
              </div>
              <div className="divide-y">
                {list.map(r => (
                  <div key={r.id} className="flex items-center justify-between gap-2 py-2">
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{r.students?.first_name} {r.students?.last_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{r.students?.student_number}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="default" disabled={busyId === r.id} onClick={() => approve(r)}>
                        <Check className="w-4 h-4 mr-1" />Approve
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" disabled={busyId === r.id}>
                            <X className="w-4 h-4 mr-1" />Reject
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Reject this scan?</AlertDialogTitle>
                            <AlertDialogDescription>This will permanently remove the pending record. Optionally add a reason.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <div className="space-y-2">
                            <Label className="text-xs">Reason (optional)</Label>
                            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. duplicate scan, wrong student" />
                          </div>
                          <AlertDialogFooter>
                            <AlertDialogCancel onClick={() => setRejectReason("")}>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => reject(r)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Reject</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
